const GA_MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim();
const GTM_CONTAINER_ID = (import.meta.env.VITE_GTM_CONTAINER_ID || '').trim();

const isBrowser = typeof window !== 'undefined';

let analyticsEnabled = false;
let gtagScriptAppended = false;
let gtmScriptAppended = false;

function getCookieDomain() {
  if (!isBrowser) {
    return undefined;
  }

  if (window.location.hostname.includes('vercel.app')) {
    return 'lykke-terapien.vercel.app';
  }

  return 'lykketerapien.dk';
}

function appendScript(src: string, attributes: Record<string, string> = {}) {
  if (!isBrowser) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  Object.entries(attributes).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });
  document.head.appendChild(script);
}

function appendInlineScript(id: string, content: string) {
  if (!isBrowser) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('data-analytics', id);
  script.text = content;
  document.head.appendChild(script);
}

function ensureGtagInitialized() {
  if (!isBrowser || !GA_MEASUREMENT_ID) {
    return;
  }

  if (!gtagScriptAppended) {
    appendScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`, {
      'data-analytics': 'gtag-loader',
    });
    gtagScriptAppended = true;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };

  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
  });
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    cookie_domain: getCookieDomain(),
    cookie_flags: 'SameSite=Lax;Secure',
    cookie_expires: 63072000,
  });
}

function ensureGtmInitialized() {
  if (!isBrowser || !GTM_CONTAINER_ID) {
    return;
  }

  if (gtmScriptAppended) {
    return;
  }

  const snippet = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':` +
    ` new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],` +
    ` j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=` +
    ` 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);` +
    `})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`;

  appendInlineScript('gtm-snippet', snippet);
  gtmScriptAppended = true;

  if (document.body) {
    const noscript = document.createElement('noscript');
    noscript.setAttribute('data-analytics', 'gtm-noscript');

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
  }
}

export function enableAnalytics() {
  if (!isBrowser || analyticsEnabled) {
    return;
  }

  if (!GA_MEASUREMENT_ID && !GTM_CONTAINER_ID) {
    return;
  }

  ensureGtagInitialized();
  ensureGtmInitialized();
  analyticsEnabled = true;
}

function cleanupScripts(selector: string) {
  if (!isBrowser) {
    return;
  }

  document
    .querySelectorAll<HTMLScriptElement | HTMLElement>(`[data-analytics="${selector}"]`)
    .forEach((element) => element.remove());
}

export function disableAnalytics() {
  if (!isBrowser) {
    return;
  }

  if (GA_MEASUREMENT_ID) {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
  }

  cleanupScripts('gtag-loader');
  cleanupScripts('gtm-snippet');
  cleanupScripts('gtm-noscript');

  gtagScriptAppended = false;
  gtmScriptAppended = false;
  analyticsEnabled = false;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}
